<script setup lang="ts">
/**
 * HeroBlock — 主页 hero（Nexus Cyberpunk，左文右视觉双栏）
 * 转化漏斗：
 *   1. 品牌名 + 副标题 + slogan
 *   2. 痛点共鸣（1 行）+ 详细介绍（1 段）
 *   3. 1 主按钮：UA 检测命中 Chrome/Edge/Firefox → 直跳对应商店 + 「推荐」badge；
 *      国产 Chromium / iOS（不可直装）→ 「前往安装指南」跳 /install，绝不误开 Chrome 商店
 *   4. 折叠次级入口：3 浏览器 chip + 手动安装
 *   5. ✓ 卖点行
 * 右侧产品主视觉：管理控制台 mockup（ConsoleShowcase）+ popup 浮动卡（PopupMockup），
 * 纯代码 mockup 直出（不再请求后端 screenshots 静态图）。
 * SSG 预渲染阶段（无 UA）主按钮只显示通用文案、不显示推荐徽章，避免假推荐。
 */
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import BrandMark from '@/components/BrandMark.vue'
import PlatformIcon from '@/components/PlatformIcon.vue'
import YfbIcon from '@/components/YfbIcon.vue'
import ConsoleShowcase from '@/components/ConsoleShowcase.vue'
import PopupMockup from '@/components/PopupMockup.vue'
import { type BrowserPlatform } from '@/config/brand'
import { useDownload } from '@/composables/useDownload'
import { detectBrowserInfo } from '@/utils/browserDetect'

const { t, tm } = useI18n()
const router = useRouter()
const { openStore } = useDownload()

/**
 * 主按钮三态：
 *   pending — 未检测（SSG / 水合前）：通用文案，点击兜底去 /install
 *   store   — 命中可直装浏览器：直跳对应商店 + 推荐 badge
 *   guide   — 国产 Chromium / iOS 等不可直装：跳 /install
 */
const ctaState = ref<'pending' | 'store' | 'guide'>('pending')
const storeTarget = ref<BrowserPlatform>('chrome')

onMounted(() => {
  const info = detectBrowserInfo()
  if (info.canInstall) {
    ctaState.value = 'store'
    storeTarget.value = info.platform as BrowserPlatform
  } else {
    ctaState.value = 'guide'
  }
})

const primaryLabel = computed(() => {
  if (ctaState.value === 'store') {
    return t('home.hero.cta.detectedCta', {
      browser: t(`home.hero.cta.stores.${storeTarget.value}`)
    })
  }
  return t(ctaState.value === 'guide' ? 'home.hero.cta.installGuide' : 'home.hero.cta.storeGeneric')
})

const fallbackHint = computed(() =>
  ctaState.value === 'store'
    ? t('home.hero.cta.fallbackHint', {
        browser: t(`home.hero.cta.stores.${storeTarget.value}`)
      })
    : t('home.hero.cta.fallbackHintGeneric')
)

function handlePrimary() {
  if (ctaState.value === 'store') {
    openStore(storeTarget.value)
  } else {
    // pending（水合间隙）/ guide 统一去安装页，宁慢勿错
    router.push('/install')
  }
}

const fallbackOpen = ref(false)

const browsers: BrowserPlatform[] = ['chrome', 'edge', 'firefox']

function goToInstall() {
  router.push('/install/manual')
}
</script>

<template>
  <section id="hero" class="hero yfb-fx-scanline">
    <div class="hero__panel yfb-glass-strong yfb-fx-grid-8">
      <div class="hero__aurora yfb-fx-aurora-band" aria-hidden="true"></div>

      <div class="hero__inner">
        <!-- 左：文案与 CTA -->
        <div class="hero__content">
          <div class="hero__brand">
            <BrandMark size="2xl" variant="full" align="left" />
          </div>

          <p class="hero__subtitle">{{ t('home.hero.subtitle') }}</p>

          <p class="hero__intro">{{ t('home.hero.intro') }}</p>

          <!-- 1 主按钮：UA 检测命中 → 直跳对应商店；不可直装 → 前往安装指南 -->
          <button
            type="button"
            class="hero__primary yfb-press yfb-fx-step"
            @click="handlePrimary"
          >
            <PlatformIcon v-if="ctaState === 'store'" :platform="storeTarget" :size="36" />
            <YfbIcon v-else name="lucide:layers" :size="30" class="hero__primary-guide-icon" />
            <span class="hero__primary-label">{{ primaryLabel }}</span>
            <span v-if="ctaState === 'store'" class="hero__primary-badge">
              ★ {{ t('home.hero.cta.recommendedFor') }}
            </span>
          </button>

          <!-- 折叠次级入口 -->
          <div class="hero__fallback">
            <button
              type="button"
              class="hero__fallback-toggle"
              :aria-expanded="fallbackOpen"
              @click="fallbackOpen = !fallbackOpen"
            >
              {{ fallbackHint }}
              <span class="hero__fallback-arrow" :class="{ 'hero__fallback-arrow--open': fallbackOpen }">
                ▼
              </span>
            </button>

            <div v-if="fallbackOpen" class="hero__fallback-panel yfb-glass">
              <div class="hero__browsers">
                <button
                  v-for="b in browsers"
                  :key="b"
                  type="button"
                  class="hero__browser yfb-press"
                  @click="openStore(b)"
                >
                  <PlatformIcon :platform="b" :size="32" />
                  <span class="hero__browser-label">
                    {{ t(`install.store.browsers.${b}`) }}
                  </span>
                  <span class="hero__browser-arrow" aria-hidden="true">↗</span>
                </button>
              </div>

              <div class="hero__manual-row">
                <div class="hero__manual-text">
                  <span class="hero__manual-label">{{ t('home.hero.cta.manualCta') }}</span>
                  <span class="hero__manual-hint">{{ t('home.hero.cta.manualHint') }}</span>
                </div>
                <button
                  type="button"
                  class="hero__manual-btn yfb-press"
                  @click="goToInstall"
                >
                  {{ t('home.hero.cta.crx') }} →
                </button>
              </div>
            </div>
          </div>

          <ul class="hero__sellpoints">
            <li v-for="point in (tm('home.hero.sellPoints') as string[])" :key="point" class="hero__sellpoint">
              <span class="hero__check" aria-hidden="true">✓</span>
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>

        <!-- 右：产品主视觉（代码 mockup 直出，不再请求后端截图） -->
        <div class="hero__visual">
          <div class="hero__mock">
            <ConsoleShowcase class="hero__console" />
            <PopupMockup class="hero__popup" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  padding: var(--yfb-sp-9) var(--yfb-sp-6);
}

.hero__panel {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
  padding: var(--yfb-sp-8) var(--yfb-sp-7);
}

.hero__aurora {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.9;
}

.hero__inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.02fr) minmax(0, 0.98fr);
  gap: var(--yfb-sp-8);
  align-items: center;
}

.hero__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--yfb-sp-4);
  min-width: 0;
}

.hero__brand {
  margin-bottom: 4px;
}

.hero__subtitle {
  font-size: clamp(var(--yfb-fs-xl), 2.2vw, var(--yfb-fs-2xl));
  font-weight: 600;
  color: var(--yfb-ink);
  margin: 0;
  text-wrap: balance;
}

.hero__intro {
  font-size: var(--yfb-fs-base);
  font-weight: 400;
  line-height: var(--yfb-lh-lead);
  color: var(--yfb-text-secondary);
  margin: 0;
  max-width: 56ch;
  text-wrap: pretty;
}

/* ===== 1 主按钮（渐变 CTA + 霓虹光晕） ===== */
.hero__primary {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-4);
  padding: var(--yfb-sp-4) var(--yfb-sp-6);
  font-family: inherit;
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
  cursor: pointer;
  margin-top: 8px;
}

.hero__primary-label {
  white-space: nowrap;
}

/* guide/pending 态：lucide 线性图标替代平台 logo，用主色保持视觉重量一致 */
.hero__primary-guide-icon {
  color: var(--yfb-accent);
  flex-shrink: 0;
  filter: drop-shadow(0 0 6px var(--yfb-glow));
}

.hero__primary-badge {
  font-size: 10px;
  font-weight: 700;
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  letter-spacing: var(--yfb-ls-mono);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  white-space: nowrap;
}

/* ===== 折叠次级 ===== */
.hero__fallback {
  width: 100%;
  max-width: 520px;
}

.hero__fallback-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  background-color: transparent;
  border: none;
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  letter-spacing: var(--yfb-ls-label);
  cursor: pointer;
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  border-radius: var(--yfb-radius-sm);
  transition: color var(--yfb-anim-fast) ease-out;
}

.hero__fallback-toggle:hover {
  color: var(--yfb-accent);
  text-shadow: 0 0 10px var(--yfb-glow);
}

.hero__fallback-arrow {
  font-size: 10px;
  transition: transform var(--yfb-anim-fast) ease-out;
}

.hero__fallback-arrow--open {
  transform: rotate(180deg);
}

.hero__fallback-panel {
  margin-top: 12px;
  padding: var(--yfb-sp-5);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-4);
}

.hero__browsers {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--yfb-sp-3);
}

.hero__browser {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-4) var(--yfb-sp-3);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out, transform var(--yfb-anim-fast) ease-out;
}

.hero__browser:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
  transform: translateY(-1px);
}

.hero__browser-label {
  font-size: var(--yfb-fs-md);
}

.hero__browser-arrow {
  font-size: var(--yfb-fs-md);
  color: var(--yfb-accent);
}

.hero__manual-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-4);
  padding-top: 12px;
  border-top: 1px solid var(--yfb-rule-soft);
}

.hero__manual-text {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-1);
  flex: 1;
  min-width: 0;
}

.hero__manual-label {
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-ink);
}

.hero__manual-hint {
  font-size: var(--yfb-fs-xs);
  font-weight: 400;
  color: var(--yfb-ink-soft);
  line-height: 1.4;
}

.hero__manual-btn {
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  letter-spacing: var(--yfb-ls-button);
  cursor: pointer;
  white-space: nowrap;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.hero__manual-btn:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
}

.hero__manual-btn:focus-visible {
  outline: 2px solid var(--yfb-focus);
  outline-offset: 2px;
}

/* ===== 卖点行 ===== */
.hero__sellpoints {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yfb-sp-2) var(--yfb-sp-5);
  margin: var(--yfb-sp-2) 0 0;
  padding: 0;
  list-style: none;
}

.hero__sellpoint {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-ink);
}

.hero__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
  border-radius: var(--yfb-radius-sm);
  box-shadow: 0 0 8px var(--yfb-glow);
}

/* ===== 右侧产品主视觉 ===== */
.hero__visual {
  min-width: 0;
}

.hero__mock {
  position: relative;
  padding-bottom: var(--yfb-sp-6);
}

.hero__console {
  width: 100%;
}

.hero__popup {
  position: absolute;
  right: -10px;
  bottom: 0;
}

@media (max-width: 1023px) {
  .hero__inner {
    grid-template-columns: 1fr;
    gap: var(--yfb-sp-7);
  }

  .hero__popup {
    right: 0;
  }
}

@media (max-width: 767px) {
  .hero {
    padding: var(--yfb-sp-6) var(--yfb-sp-4) var(--yfb-sp-9);
  }
  .hero__panel {
    padding: var(--yfb-sp-7) var(--yfb-sp-4);
  }
  .hero__primary {
    width: 100%;
    justify-content: center;
    font-size: var(--yfb-fs-sm);
    padding: var(--yfb-sp-4);
  }
  .hero__primary-label {
    white-space: normal;
  }
  .hero__browsers {
    grid-template-columns: 1fr;
  }
  .hero__manual-row {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .hero__manual-text {
    text-align: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero__fallback-arrow,
  .hero__browser,
  .hero__fallback-toggle,
  .hero__manual-btn {
    transition: none;
  }
}
</style>
